import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'

import { getNotifications, markNotificationsRead } from '../../api/notifications'
import routes from '../../routers'
import UserPhoto from '../UserPhoto'

import './style.css'

const CHECK_EVERY_MS = 60 * 1000

const describe = ({ type, actor, recipe_name }) => {
  if (type === 'recipe_added_for_me') {
    return (
      <>
        <b>{actor.fullname}</b> added your <b>{recipe_name}</b> recipe for you
      </>
    )
  }

  if (type === 'reply_to_my_comment') {
    return (
      <>
        <b>{actor.fullname}</b> replied to your comment on <b>{recipe_name}</b>
      </>
    )
  }

  if (type === 'comment_on_my_recipe') {
    return (
      <>
        <b>{actor.fullname}</b> commented on your recipe <b>{recipe_name}</b>
      </>
    )
  }

  return (
    <>
      <b>{actor.fullname}</b> commented on <b>{recipe_name}</b>
    </>
  )
}

const BellIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 10a6 6 0 1 1 12 0c0 3.5 1 5.5 2 6.5H4c1-1 2-3 2-6.5Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M10 19.5a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.8" />
  </svg>
)

const NotificationBell = ({ className = '' }) => {
  const navigate = useNavigate()
  const wrapRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unread, setUnread] = useState(0)

  const apply = ({ data }) => {
    setNotifications(data?.notifications || [])
    setUnread(data?.unread_count || 0)
  }

  const refresh = useCallback(
    () =>
      getNotifications()
        .then(apply)
        .catch(() => {}),
    []
  )

  useEffect(() => {
    refresh()
    const timer = setInterval(() => !document.hidden && refresh(), CHECK_EVERY_MS)
    return () => clearInterval(timer)
  }, [refresh])

  useEffect(() => {
    if (!open) {
      return
    }

    const close = (e) => {
      if (e.type === 'keydown' ? e.key === 'Escape' : !wrapRef.current?.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', close)
    }
  }, [open])

  const toggle = () => {
    if (!open) {
      refresh()
    }
    setOpen(!open)
  }

  const openNotification = (notification) => {
    setOpen(false)
    if (!notification.read) {
      markNotificationsRead([notification.id])
        .then(apply)
        .catch(() => {})
    }
    navigate(notification.path)
  }

  const markAllRead = () =>
    markNotificationsRead()
      .then(apply)
      .catch(() => {})

  return (
    <div className={`notificationBell ${className}`} ref={wrapRef}>
      <button
        type="button"
        className="notificationBellButton"
        onClick={toggle}
        aria-expanded={open}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
      >
        <BellIcon />
        {unread ? <span className="notificationBadge">{unread > 9 ? '9+' : unread}</span> : null}
      </button>

      {open ? (
        <div className="notificationPanel" role="dialog" aria-label="Notifications">
          <div className="notificationPanelHeader">
            <span className="notificationPanelTitle">Notifications</span>
            {unread ? (
              <button type="button" className="notificationTextButton" onClick={markAllRead}>
                Mark all read
              </button>
            ) : null}
          </div>

          {notifications.length ? (
            <ul className="notificationList">
              {notifications.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    className={`notificationItem ${notification.read ? '' : 'notificationUnread'}`}
                    onClick={() => openNotification(notification)}
                  >
                    <UserPhoto
                      image={notification.actor.avatar}
                      name={(notification.actor.fullname || '?')[0]}
                      classStylePhoto="notificationAvatar"
                    />
                    <span className="notificationText">
                      <span className="notificationSummary">{describe(notification)}</span>
                      {notification.excerpt ? (
                        <span className="notificationExcerpt">"{notification.excerpt}"</span>
                      ) : null}
                      <span className="notificationTime">
                        {notification.group_name} · {moment(notification.created_at).fromNow()}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="notificationEmpty">
              Nothing yet. When someone comments on your recipes or replies to you, it shows up
              here.
            </p>
          )}

          <button
            type="button"
            className="notificationTextButton notificationSettingsLink"
            onClick={() => {
              setOpen(false)
              navigate(routes.notification)
            }}
          >
            Notification settings
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default NotificationBell
